/* The freshness view. Read-only except one admin action: "Include again" on
 * an excluded table, which calls the control room's admin route
 * (POST /api/v1/refresh-policy/include) -- a door onto that write surface,
 * not a new one.
 *
 * Tenant-gated, not admin-gated: watching how fresh the mirror is changes
 * nothing, so it sits with the catalog views rather than in the control
 * room. Editing a cadence is a stored instruction that changes what runs
 * unattended on the ERP, and that pane lives in sync-control.jsx behind the
 * admin gate.
 *
 * Every Priority-sourced value renders dir="auto" per element. Never
 * dir="rtl" on a container of mixed content -- that moves the punctuation
 * of the LTR parts too.
 */
import { RefreshIcon } from './icons.jsx';
import { Modal } from './accessibility.jsx';
import { pageEnv, pickPending } from './company-pick.mjs';

const { useState, useEffect, useCallback, useRef } = React;

const mfCls = (...xs) => xs.filter(Boolean).join(' ');

const T = {
  title: { he: 'טריות המראה', en: 'Mirror freshness' },
  fresh: { he: 'עדכני', en: 'Fresh' },
  due: { he: 'ממתין לרענון', en: 'Due' },
  excluded: { he: 'מוחרגת', en: 'Excluded' },
  failing: { he: 'נכשל', en: 'Failing' },
  running: { he: 'רץ', en: 'Running' },
  parked: { he: 'נזנח', en: 'Parked' },
  blocked: { he: 'חסום', en: 'Blocked' },
  tables: { he: 'טבלאות', en: 'Tables' },
  oldest: { he: 'הישן ביותר', en: 'Oldest refresh' },
  noRun: { he: 'לא רץ מעבר כרגע', en: 'No pass running' },
  passRunning: { he: 'מעבר פעיל', en: 'Pass running' },
  currentSource: { he: 'עובד כעת על', en: 'Working on' },
  table: { he: 'טבלה', en: 'Table' },
  state: { he: 'מצב', en: 'State' },
  mode: { he: 'מצב רענון', en: 'Mode' },
  rows: { he: 'שורות', en: 'Rows' },
  last: { he: 'רענון אחרון', en: 'Last refreshed' },
  checked: { he: 'נבדק', en: 'Checked' },
  next: { he: 'רענון הבא', en: 'Next due' },
  interval: { he: 'מרווח', en: 'Interval' },
  setAdaptive: { he: 'אוטומטי', en: 'adaptive' },
  setPerson: { he: 'ידני', en: 'manual' },
  setDefault: { he: 'ברירת מחדל', en: 'default' },
  ratioTip: { he: 'גיל הטבלה חלקי המרווח שלה', en: "age divided by the table's own interval" },
  why: { he: 'סיבה', en: 'Reason' },
  drift: { he: 'שינויי מבנה', en: 'Structure drift' },
  blocking: { he: 'חוסם טעינה', en: 'blocks loading' },
  keyChanged: { he: 'מפתח השתנה', en: 'key changed' },
  reload: { he: 'רענן', en: 'Reload' },
  none: { he: 'אין', en: 'none' },
  repairing: { he: 'מתקן', en: 'repairing' },
  all: { he: 'הכל', en: 'All' },
  company: { he: 'חברה', en: 'Company' },
  noEnvs: { he: 'עדיין אין ריצות טבלה עבור לקוח זה.', en: 'No table runs recorded for this tenant yet.' },
  noRows: { he: 'אין טבלאות במצב זה.', en: 'No tables in this state.' },
  includeAgain: { he: 'החזר לסנכרון', en: 'Include again' },
  including: { he: 'מחזיר לסנכרון…', en: 'Including…' },
  cancel: { he: 'ביטול', en: 'Cancel' },
  since: { he: 'מוחרגת מאז', en: 'Excluded since' },
  by: { he: 'על ידי', en: 'by' },
  byPerson: { he: 'אדם', en: 'a person' },
  byAdaptive: { he: 'הכלל האוטומטי', en: 'the adaptive rule' },
  includeNamed: { he: 'החזר לסנכרון: {tname}', en: 'Include again: {tname}' },
  actions: { he: 'פעולות', en: 'Actions' },
  crashShrunk: { he: 'החלון הוקטן אחרי קריסה', en: 'Window shrunk after a crash' },
  structureChanged: { he: 'מבנה השתנה', en: 'structure changed' },
};
// Falls back to the key itself rather than throwing: an unknown key here
// must not unmount the whole view the way React error #31 does elsewhere
// in this app (see form-view.jsx's fvDropLine comment).
const t = (k, lang) => (T[k] || {})[lang === 'he' ? 'he' : 'en'] || k;

// Whole seconds under a minute, whole minutes under an hour, hours under two days, else days; Hebrew units are דק׳ / ש׳ / י׳.
function fmtInterval(sec, lang) {
  if (sec == null) return '—';
  const he = lang === 'he';
  if (sec < 60) return `${Math.round(sec)} ${he ? 'שנ׳' : 's'}`;
  if (sec < 3600) return `${Math.round(sec / 60)} ${he ? 'דק׳' : 'min'}`;
  if (sec < 2 * 86400) return `${Math.round(sec / 3600 * 10) / 10} ${he ? 'ש׳' : 'h'}`;
  return `${Math.round(sec / 86400 * 10) / 10} ${he ? 'י׳' : 'd'}`;
}
const SET_BY_KEY = { adaptive: 'setAdaptive', person: 'setPerson' };
const EXCLUDED_BY_KEY = { adaptive: 'byAdaptive', person: 'byPerson' };

const STATES = ['fresh', 'due', 'excluded', 'running', 'failing', 'parked', 'blocked'];

function Rollup({ rollup, lang }) {
  return (
    <div className="kpi-rail">
      {STATES.map((k) => (
        <div className={`kpi st-${k}`} key={k}>
          <div className="label">{t(k, lang)}</div>
          <div className="value">{rollup[k] == null ? '—' : rollup[k].toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function LivePass({ run, lang }) {
  if (!run) return <div className="card"><div className="card-body muted">{t('noRun', lang)}</div></div>;
  const pct = run.posts_budget
    ? Math.min(100, Math.round((run.posts_spent / run.posts_budget) * 100))
    : 0;
  return (
    <div className="card mf-pass">
      <div>
        <div className="mf-pass-label">{t('passRunning', lang)}</div>
        <div className="mf-pass-n">{run.tables_done} <span className="of">/ {run.tables_planned}</span></div>
      </div>
      <div>
        <div className="mf-bar"><div className="mf-bar-fill" style={{ width: `${pct}%` }}/></div>
        <div className="mf-pass-meta">
          <span>
            {run.current_source && <>{t('currentSource', lang)} <span className="mono" dir="auto" style={{ color: 'var(--ink)' }}>{run.current_source}</span></>}
          </span>
          <span>{run.posts_spent} / {run.posts_budget} posts</span>
        </div>
      </div>
      <span className="pill st-info"><span className="dot live"/>{t('running', lang)}</span>
    </div>
  );
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

function IncludeDialog({ row, env, tenantId, lang, onDone, onClose, onConflict }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const he = lang === 'he';
  const label = `${t('includeAgain', lang)}: ${row.tname}`;
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await PCMAdapter.includeRefreshTable(tenantId, env, row.tname);
      onDone(row.tname);
    } catch (e) {
      if (e && e.status === 409 && onConflict) onConflict();
      const d = e && e.detail;
      setError(typeof d === 'string' && d ? d : String(e && e.message ? e.message : e));
      setBusy(false);
    }
  };
  const when = <span dir="ltr">{fmtTime(row.finished_at)}</span>;
  return (
    <Modal label={label} onClose={busy ? () => {} : onClose}>
      <h2 className="page-title" style={{ fontSize: 16 }}>{t('includeAgain', lang)}</h2>
      <div>
        <span className="mono" dir="auto">{row.tname}</span> · {t('company', lang)} <span className="mono" dir="auto">{env}</span>
      </div>
      <div>
        {t('why', lang)}: <span dir="auto">{row.excluded_reason || '—'}</span>
      </div>
      <div className="muted">
        {t('since', lang)} <span dir="ltr">{fmtTime(row.excluded_at)}</span>
        {row.excluded_by && <> {t('by', lang)} {t(EXCLUDED_BY_KEY[row.excluded_by] || 'setDefault', lang)}</>}
      </div>
      <div>
        {he
          ? <>היא תיסרק שוב במעבר הבא של <span dir="auto">{env}</span>, מההתחלה. העותק מ-{when} נשאר במראה עד שהסריקה החדשה תחליף אותו.</>
          : <>It walks again in <span dir="auto">{env}</span>'s next pass, from the start. The copy from {when} stays in the mirror until the new walk replaces it.</>}
      </div>
      {row.refresh_failures > 0 && (
        <div className="st-warn" style={{ padding: '6px 10px', borderRadius: 6 }}>
          {he ? 'הרענון האחרון שלה נכשל: ' : 'Its last refresh failed: '}<span dir="auto">{row.refresh_detail || '—'}</span>
        </div>
      )}
      {error && <div role="alert" dir="auto" style={{ color: 'var(--st-danger-fg)' }}>{error}</div>}
      <div className="dlg-actions">
        <button type="button" className="btn" disabled={busy} onClick={onClose}>{t('cancel', lang)}</button>
        <button type="button" className="btn primary" disabled={busy} onClick={confirm}>
          {busy ? t('including', lang) : t('includeAgain', lang)}
        </button>
      </div>
    </Modal>
  );
}

function MirrorFreshnessPage({ lang, companyPick = null }) {
  const [envState, setEnv] = useState(undefined);
  // Offline picker spec §4.4: the offline workspace reads the top-bar pick (its upload's env, or a
  // company with no upload's own env -- no rows, never another company's), and this page's select hides.
  // While the picker loads (P16) nothing is asked: with no env the server answers its own company.
  const pending = pickPending(companyPick);
  const env = companyPick && !pending ? pageEnv(companyPick.pick) : envState;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('');
  const [offset, setOffset] = useState(0);
  // A new pick starts at its first page with none of the old company's rows on screen: reset during render,
  // so the load below asks once, at offset 0 (an effect would ask first with the old offset).
  // A request generation counter: if env/filter/offset change (or Reload
  // fires) while an earlier request is still in flight, that earlier
  // response must not overwrite state for a selection the operator has
  // already moved past.
  const reqIdRef = useRef(0);
  const [pickedEnv, setPickedEnv] = useState(env);
  if (companyPick && env !== pickedEnv) {
    // Invalidate now: the old company's answer may land before the load effect below bumps the counter.
    reqIdRef.current += 1;
    setPickedEnv(env); setOffset(0); setData(null);
  }
  const [including, setIncluding] = useState(null);
  const [notice, setNotice] = useState(null);
  const noticeRef = useRef(null);
  const LIMIT = 100;

  const load = useCallback(async () => {
    if (pending) return;
    const myId = ++reqIdRef.current;
    try {
      // A named adapter method, matching every other page here. env is
      // OPTIONAL -- the server resolves it and returns both the env it
      // used and the full list, because the dashboard has no env concept
      // to supply one from.
      const result = await PCMAdapter.getRefreshStatus({
        env, limit: LIMIT, offset, state: filter || undefined,
      });
      if (myId !== reqIdRef.current) return; // superseded by a later call
      setData(result);
      setErr(null);
    } catch (e) {
      if (myId !== reqIdRef.current) return;
      setErr(String(e && e.message ? e.message : e));
    }
  }, [env, filter, offset, pending]);

  useEffect(() => { load(); }, [load]);
  // Poll only while a pass is actually running: a 10s poll against a
  // 3,233-row query all day is a cost with no reader. Keyed on the
  // boolean, not `data` itself -- `data` gets a new reference on every
  // load (including every poll tick), which would tear down and recreate
  // the interval every 10s instead of running one stable interval for the
  // whole time a pass is up.
  const isAdmin = PCMAdapter.getRole() === 'admin';
  const running = !!(data && data.run);
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [running, load]);

  if (err) return <div role="alert" className="error-pane" dir="auto">{err}</div>;
  if (!data) return <div className="card"><div className="card-body dim">…</div></div>;

  const head = (sub) => (
    <div className="page-head">
      <div>
        <h1 className="page-title">{t('title', lang)}</h1>
        {sub && <div className="page-sub">{sub}</div>}
      </div>
      <div className="page-actions">
        {!companyPick && data.envs.length > 1 && (
          <select className="select" style={{ width: 'auto' }} value={data.env}
                  aria-label={t('company', lang)}
                  onChange={(e) => { setEnv(e.target.value); setOffset(0); }}>
            {data.envs.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
        )}
        <button type="button" className="btn" onClick={load}><RefreshIcon/> {t('reload', lang)}</button>
      </div>
    </div>
  );

  if (data.envs.length === 0) {
    return (
      <>
        {head(null)}
        <div className="card"><div className="card-body muted" dir="auto">{t('noEnvs', lang)}</div></div>
      </>
    );
  }

  const r = data.rollup;
  const sub = <>{r.tables == null ? '—' : Number(r.tables).toLocaleString()} {t('tables', lang).toLowerCase()}{r.oldest_finished_at && <> · {t('oldest', lang).toLowerCase()} <span dir="ltr">{new Date(r.oldest_finished_at).toLocaleString()}</span></>}</>;

  return (
    <>
      {head(sub)}
      <div role="status" aria-live="polite" ref={noticeRef} tabIndex={-1} className={notice ? 'card' : undefined}>
        {notice && (
          <div className="card-body">
            <span className="mono" dir="auto">{notice}</span>
            {lang === 'he' ? ' הוחזרה לסנכרון וממתינה לרענון.' : ' is included again and due now.'}
          </div>
        )}
      </div>
      <Rollup rollup={data.rollup} lang={lang}/>
      <LivePass run={data.run} lang={lang}/>

      <div className="card">
        <div className="card-head pills" role="group">
          {['', ...STATES].map((f) => (
            <button type="button" key={f || 'all'}
                    className={mfCls('tab', filter === f && 'active')}
                    onClick={() => { setFilter(f); setOffset(0); }}
            >{f ? t(f, lang) : t('all', lang)}</button>
          ))}
        </div>
        <div className="tbl-wrap" tabIndex={0}>
          <table className="tbl">
            <thead><tr>
              <th>{t('table', lang)}</th><th>{t('state', lang)}</th>
              <th>{t('mode', lang)}</th><th className="num">{t('rows', lang)}</th>
              <th>{t('last', lang)}</th><th>{t('checked', lang)}</th>
              <th>{t('next', lang)}</th><th>{t('interval', lang)}</th>
              <th>{t('why', lang)}</th><th style={{ position: 'relative' }}><span className="sr-only">{t('actions', lang)}</span></th>
            </tr></thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.tname}>
                  <td className="mono" dir="auto" style={{ fontWeight: 500, color: 'var(--ink)' }}>{row.tname}</td>
                  <td><span className={`chip st-${row.state}`}><span className="dot"/>{t(row.state, lang)}</span></td>
                  <td className="mono">{row.mode}</td>
                  <td className="mono num">{row.rows_total != null ? row.rows_total.toLocaleString() : '—'}</td>
                  <td className="muted" dir="ltr">{row.finished_at ? new Date(row.finished_at).toLocaleString() : '—'}</td>
                  <td className="muted" dir="ltr">{row.checked_at ? new Date(row.checked_at).toLocaleString() : '—'}</td>
                  <td className="muted" dir="ltr">{row.next_due_at ? new Date(row.next_due_at).toLocaleString() : '—'}</td>
                  <td className="muted">
                    <span className="mono" dir="auto">{row.excluded_at ? '—' : fmtInterval(row.interval_s, lang)}</span>
                    {' '}{t(SET_BY_KEY[row.excluded_at ? row.excluded_by : row.set_by] || 'setDefault', lang)}
                    {row.overdue_ratio != null && (
                      <> <span className="mono" dir="ltr" title={t('ratioTip', lang)}>×{row.overdue_ratio}</span></>
                    )}
                  </td>
                  <td className="muted" dir="auto">{row.excluded_at
                    ? (row.excluded_reason || '')
                    : row.repair_lo != null
                      ? `${t('repairing', lang)} ${row.repair_lo}..${row.repair_hi} (${row.repair_windows})`
                      : (row.refresh_detail && row.refresh_detail !== row.crash_note
                        ? row.refresh_detail
                        : (!row.refresh_detail && row.structure_changed_at ? t('structureChanged', lang) : ''))}
                    {row.crash_note && (
                      <div>{t('crashShrunk', lang)}: <span dir="auto">{row.crash_note}</span></div>
                    )}</td>
                  <td>{isAdmin && row.state === 'excluded' && !row.copied_from && (
                    <button type="button" className="btn btn-sm"
                            aria-label={t('includeNamed', lang).replace('{tname}', row.tname)} onClick={() => setIncluding(row)}>{t('includeAgain', lang)}</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.rows.length === 0 && <div className="card-body muted">{t('noRows', lang)}</div>}
        <div className="pager">
          <span className="page-info">{data.total === 0 ? 0 : offset + 1}–{offset + data.rows.length} / {data.total}</span>
          <div className="arrows">
            <button type="button" disabled={offset === 0} aria-label={lang === 'he' ? 'העמוד הקודם' : 'Previous page'}
                    onClick={() => setOffset(Math.max(0, offset - LIMIT))}>‹</button>
            <button type="button" disabled={offset + LIMIT >= data.total} aria-label={lang === 'he' ? 'העמוד הבא' : 'Next page'}
                    onClick={() => setOffset(offset + LIMIT)}>›</button>
          </div>
        </div>
      </div>

      {including && (
        <IncludeDialog row={including} env={data.env} tenantId={data.tenant_id} lang={lang}
                       onClose={() => setIncluding(null)} onConflict={load}
                       onDone={async (tname) => {
                         setIncluding(null);
                         setNotice(tname);
                         await load();
                         // The trigger button is gone (the row is due now): move focus to the confirmation.
                         if (noticeRef.current) noticeRef.current.focus();
                       }}/>
      )}

      <div className="card">
        <div className="card-head">{t('drift', lang)}</div>
        <div className="card-body">
          {data.structure.length === 0
            ? <div className="muted">{t('none', lang)}</div>
            : data.structure.map((s, i) => (
                <div key={i} className="mf-drift-row">
                  <span className="mono" dir="auto">{s.ename}</span>
                  {s.added && <span className="chip st-ok">+{s.added}</span>}
                  {s.dropped && <span className="chip st-danger">−{s.dropped}</span>}
                  {s.type_changed && <span className="chip st-warn">~{s.type_changed}</span>}
                  {/* key_changed is a bool, unlike the three text-list fields
                      above -- rendering it directly would print the literal
                      word "true". */}
                  {s.key_changed && <span className="chip st-warn">⚷ {t('keyChanged', lang)}</span>}
                  {s.blocking && <strong>({t('blocking', lang)})</strong>}
                </div>
              ))}
        </div>
      </div>
    </>
  );
}

window.MirrorFreshnessPage = MirrorFreshnessPage;
